import React, { useState, useEffect } from 'react';
import { Save, Check } from 'lucide-react';
import { useSettings } from '../../hooks/useSettings';
import toast from 'react-hot-toast';

const PRESCRIPTION_TEMPLATES = [
  { id: 'template1', name: 'Template 1 - Classic', description: 'Traditional prescription layout with doctor info at top' },
  { id: 'template2', name: 'Template 2 - Modern', description: 'Contemporary design with sections (H/O, C/C, O/E, etc.)' },
  { id: 'template3', name: 'Template 3 - Detailed', description: 'Comprehensive format with investigation and treatment plan' },
  { id: 'easy-touch', name: 'Easy Touch Hospital', description: 'Branded hospital template with watermark' },
];

export default function PrescriptionTemplateSettings() {
  const { settings, loading, updateSettings } = useSettings();
  const [selectedTemplate, setSelectedTemplate] = useState('template1');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setSelectedTemplate(settings.prescription_template || 'template1');
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);

    const result = await updateSettings({
      prescription_template: selectedTemplate,
    });

    if (result.success) {
      toast.success('Prescription template updated successfully');
    } else {
      toast.error(result.error || 'Failed to update template');
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Prescription Template</h3>
        <p className="text-sm text-gray-600 mb-6">
          Choose the template that will be used for all doctor prescriptions.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PRESCRIPTION_TEMPLATES.map((template) => (
            <div
              key={template.id}
              onClick={() => setSelectedTemplate(template.id)}
              className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                selectedTemplate === template.id
                  ? 'border-teal-600 bg-teal-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {selectedTemplate === template.id && (
                <div className="absolute top-2 right-2 w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}

              <div className="aspect-[3/4] bg-white border border-gray-200 rounded mb-3 flex items-center justify-center">
                <div className="text-center p-4">
                  <div className="text-xs text-gray-400 mb-2">Preview</div>
                  <div className="text-2xl font-bold text-gray-300">{template.name}</div>
                </div>
              </div>

              <h4 className="font-semibold text-gray-900 mb-1">{template.name}</h4>
              <p className="text-sm text-gray-600">{template.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || selectedTemplate === settings.prescription_template}
          className="flex items-center space-x-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save Template'}</span>
        </button>
      </div>
    </div>
  );
}
